import React, { useState, useEffect } from 'react';
import QuoteActions from './QuoteActions';
import InsightSection from './InsightSection';
import ShareQuote from './ShareQuote';
import { getTodayDate } from '../utils/dateUtils';
import { translateText, getDeviceLanguage } from '../utils/translationUtils';

type Quote = {
  id: number;
  text: string;
  author: string;
  date: string;
};

type FavoriteQuote = Quote & {
  collectedAt: number;
  insight?: string;
};

type TodayQuoteProps = {
  quote: Quote;
  onLike: (quote: Quote) => void;
  favorites: FavoriteQuote[];
  onUpdateInsight: (id: number, insight: string) => void;
};

const TodayQuote: React.FC<TodayQuoteProps> = ({ quote, onLike, favorites, onUpdateInsight }) => {
  const [isLiked, setIsLiked] = useState(false);
  const [favoriteQuote, setFavoriteQuote] = useState<FavoriteQuote | null>(null);
  const [isTranslated, setIsTranslated] = useState(false);
  const [translatedText, setTranslatedText] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTranslationError, setShowTranslationError] = useState(false);
  const [today, setToday] = useState(getTodayDate());

  useEffect(() => {
    const favorite = favorites.find(fav => fav.id === quote.id);
    setIsLiked(!!favorite);
    setFavoriteQuote(favorite || null);
    setToday(getTodayDate());
  }, [favorites, quote]);

  const handleLike = () => {
    onLike(quote);
    setIsLiked(!isLiked);
  };

  const handleTranslate = async () => {
    if (!isTranslated) {
      setIsTranslating(true);
      try {
        const deviceLanguage = getDeviceLanguage();
        const translated = await translateText(quote.text, deviceLanguage);
        if (translated) {
          setTranslatedText(translated);
          setIsTranslated(true);
        } else {
          throw new Error('Translation failed');
        }
      } catch (error) {
        console.error('Translation error:', error);
        setShowTranslationError(true);
        setTimeout(() => setShowTranslationError(false), 3000);
      } finally {
        setIsTranslating(false);
      }
    } else {
      setIsTranslated(false);
    }
  };

  const handleUpdateInsight = (insight: string) => {
    onUpdateInsight(quote.id, insight);
  };

  return (
    <div className="bg-white rounded-lg shadow-inner p-6 relative">
      {showTranslationError && (
        <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-center py-2 px-4 rounded-t-lg">
          翻译功能当前网络条件下不可用
        </div>
      )}
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-indigo-600 font-semibold">{today}</div>
      </div>
      <blockquote className="text-2xl italic mb-4 text-gray-700">
        "{isTranslated ? translatedText : quote.text}"
      </blockquote>
      <p className="text-right text-xl text-indigo-600 font-semibold mb-4">- {quote.author}</p>
      <QuoteActions
        isLiked={isLiked}
        onLike={handleLike}
        onShare={() => setShowShareModal(true)}
        onTranslate={handleTranslate}
        isTranslating={isTranslating}
        isTranslated={isTranslated}
      />
      {isLiked && (
        <InsightSection
          insight={favoriteQuote?.insight || ''}
          onUpdateInsight={handleUpdateInsight}
        />
      )}
      {showShareModal && (
        <ShareQuote
          quote={{
            ...quote,
            insight: favoriteQuote?.insight
          }}
          isTranslated={isTranslated}
          translatedText={translatedText}
          onClose={() => setShowShareModal(false)}
        />
      )}
    </div>
  );
};

export default TodayQuote;